import { maxDimensions } from '@qronstat/shared';

export const distributionCharts = ['bar', 'column', 'pie', 'donut'] as const;
export type DistributionChart = (typeof distributionCharts)[number];

export const seriesCharts = ['bar', 'line', 'area', 'barline'] as const;
export type SeriesChart = (typeof seriesCharts)[number];

export type VisualizationRender =
  | { widget: 'table' }
  | { widget: 'kpis' }
  | { widget: 'series'; chart: SeriesChart; byDimension: DistributionChart }
  | { widget: 'distribution'; chart: DistributionChart }
  | { widget: 'heatmap' }
  | { widget: 'funnel' }
  | { widget: 'cohort' }
  | { widget: 'compare' }
  | { widget: 'sinceStart' };

export interface VisualizationSpec {
  metrics: { min: number; max: number; cubes: readonly string[] | null };
  dimensions: { min: number; max: number };
  steps: boolean;
  granularity: boolean;
  stack: boolean;
  objects?: boolean;
  render: VisualizationRender;
}

const one = { min: 1, max: 1, cubes: null };
const many = { min: 1, max: 10, cubes: null };
const none = { min: 0, max: 0 };

export const visualizationRegistry = {
  table: {
    metrics: many,
    dimensions: { min: 0, max: maxDimensions },
    steps: false,
    granularity: true,
    stack: false,
    render: { widget: 'table' },
  },
  line: {
    metrics: many,
    dimensions: { min: 0, max: 1 },
    steps: false,
    granularity: true,
    stack: true,
    render: { widget: 'series', chart: 'line', byDimension: 'column' },
  },
  bar: {
    metrics: many,
    dimensions: { min: 0, max: 1 },
    steps: false,
    granularity: true,
    stack: true,
    render: { widget: 'series', chart: 'bar', byDimension: 'bar' },
  },
  area: {
    metrics: many,
    dimensions: { min: 0, max: 1 },
    steps: false,
    granularity: true,
    stack: true,
    render: { widget: 'series', chart: 'area', byDimension: 'column' },
  },
  pie: {
    metrics: one,
    dimensions: { min: 1, max: 1 },
    steps: false,
    granularity: false,
    stack: false,
    render: { widget: 'distribution', chart: 'donut' },
  },
  column: {
    metrics: one,
    dimensions: { min: 1, max: 1 },
    steps: false,
    granularity: false,
    stack: false,
    render: { widget: 'distribution', chart: 'column' },
  },
  heatmap: {
    metrics: one,
    dimensions: none,
    steps: false,
    granularity: false,
    stack: false,
    render: { widget: 'heatmap' },
  },
  funnel: {
    metrics: { min: 0, max: 0, cubes: null },
    dimensions: none,
    steps: true,
    granularity: false,
    stack: false,
    render: { widget: 'funnel' },
  },
  cohort: {
    metrics: { min: 0, max: 1, cubes: ['cohort_activity', 'cohorts'] },
    dimensions: none,
    steps: false,
    granularity: false,
    stack: false,
    render: { widget: 'cohort' },
  },
  compare: {
    metrics: one,
    dimensions: { min: 1, max: 1 },
    steps: false,
    granularity: true,
    stack: false,
    objects: true,
    render: { widget: 'compare' },
  },
  sinceStart: {
    metrics: one,
    dimensions: { min: 1, max: 1 },
    steps: false,
    granularity: false,
    stack: false,
    objects: true,
    render: { widget: 'sinceStart' },
  },
  kpi: {
    metrics: many,
    dimensions: none,
    steps: false,
    granularity: false,
    stack: false,
    render: { widget: 'kpis' },
  },
} as const satisfies Record<string, VisualizationSpec>;

export type VisualizationId = keyof typeof visualizationRegistry;

export const visualizationIds = Object.keys(visualizationRegistry) as [VisualizationId, ...VisualizationId[]];

export type VisualizationProblem = 'needMetric' | 'needDimension' | 'tooManyMetrics' | 'tooManyDimensions';

export interface VisualizationInput {
  metrics: readonly string[];
  dimensions: readonly string[];
}

export function visualizationSpec(id: VisualizationId): VisualizationSpec {
  return visualizationRegistry[id];
}

export function acceptsMetric(id: VisualizationId, metricCube: string): boolean {
  const cubes = visualizationSpec(id).metrics.cubes;
  return cubes === null || cubes.includes(metricCube);
}

export function visualizationProblems(
  id: VisualizationId,
  input: VisualizationInput,
): VisualizationProblem[] {
  const spec = visualizationSpec(id);
  const out: VisualizationProblem[] = [];
  if (input.metrics.length < spec.metrics.min) out.push('needMetric');
  if (input.metrics.length > spec.metrics.max) out.push('tooManyMetrics');
  if (input.dimensions.length < spec.dimensions.min) out.push('needDimension');
  if (input.dimensions.length > spec.dimensions.max) out.push('tooManyDimensions');
  return out;
}

export function fitToVisualization<T extends VisualizationInput>(id: VisualizationId, input: T): T {
  const spec = visualizationSpec(id);
  return {
    ...input,
    metrics: input.metrics.slice(0, spec.metrics.max),
    dimensions: input.dimensions.slice(0, spec.dimensions.max),
  };
}
